import axios, { AxiosError } from 'axios';
import {
  AuthResponse,
  LoginCredentials,
  RegisterData,
  ProfileResponse,
  CompanyAvailabilityResponse,
  APIError
} from '../types/auth';

/**
 * Auth Service
 *
 * Handles all authentication-related API requests to the backend.
 */

// API Base URL from environment variable or default to localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

// Auth endpoints
const AUTH_ENDPOINTS = {
  register: `${API_BASE_URL}/auth/register`,
  login: `${API_BASE_URL}/auth/login`,
  logout: `${API_BASE_URL}/auth/logout`,
  profile: `${API_BASE_URL}/auth/profile`,
  verifyEmail: `${API_BASE_URL}/auth/verify-email`,
  forgotPassword: `${API_BASE_URL}/auth/forgot-password`,
  resetPassword: `${API_BASE_URL}/auth/reset-password`,
  checkCompany: `${API_BASE_URL}/auth/check-company`,
  resendVerification: `${API_BASE_URL}/auth/resend-verification`,
  acceptInvitation: `${API_BASE_URL}/auth/accept-invitation`,
  changePassword: `${API_BASE_URL}/auth/change-password`
};

/**
 * Set authorization header with token
 */
export function setAuthToken(token: string | null): void {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
}

/**
 * Handle API errors
 */
function handleAPIError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<APIError>;
    const errorMessage = axiosError.response?.data?.error || axiosError.message;
    const err = new Error(errorMessage);
    // Attach full response data so callers can access extra fields (e.g. company_id on trial_expired)
    (err as any).responseData = axiosError.response?.data;
    throw err;
  }
  throw error;
}

/**
 * Register a new user with self-service company creation
 */
export async function register(data: RegisterData): Promise<AuthResponse> {
  try {
    const response = await axios.post<AuthResponse>(AUTH_ENDPOINTS.register, data);
    return response.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Login with email and password
 */
export async function login(credentials: LoginCredentials): Promise<AuthResponse> {
  try {
    const response = await axios.post<AuthResponse>(AUTH_ENDPOINTS.login, credentials);
    return response.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Logout (client-side token removal)
 */
export async function logout(token: string): Promise<void> {
  try {
    await axios.post(
      AUTH_ENDPOINTS.logout,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
  } catch (error) {
    // Even if logout fails on server, we'll clear client-side token
    console.warn('Logout request failed:', error);
  }
}

/**
 * Get current user profile
 */
export async function getProfile(token: string): Promise<ProfileResponse> {
  try {
    const response = await axios.get<ProfileResponse>(AUTH_ENDPOINTS.profile, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Update current user profile
 */
export async function updateProfile(
  data: { first_name?: string; last_name?: string },
  token: string
): Promise<{ success: boolean; message: string; data: any }> {
  try {
    const response = await axios.put<{ success: boolean; message: string; data: any }>(
      AUTH_ENDPOINTS.profile,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Verify email with token
 */
export async function verifyEmail(token: string): Promise<{ success: boolean; message: string }> {
  try {
    const response = await axios.post<{ success: boolean; message: string }>(
      AUTH_ENDPOINTS.verifyEmail,
      { token }
    );
    return response.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Request password reset
 */
export async function forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
  try {
    const response = await axios.post<{ success: boolean; message: string }>(
      AUTH_ENDPOINTS.forgotPassword,
      { email }
    );
    return response.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Reset password with token
 */
export async function resetPassword(
  token: string,
  newPassword: string
): Promise<{ success: boolean; message: string }> {
  try {
    const response = await axios.post<{ success: boolean; message: string }>(
      AUTH_ENDPOINTS.resetPassword,
      {
        token,
        new_password: newPassword
      }
    );
    return response.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Resend verification email
 */
export async function resendVerification(email: string): Promise<{ success: boolean; message: string }> {
  try {
    const response = await axios.post<{ success: boolean; message: string }>(
      AUTH_ENDPOINTS.resendVerification,
      { email }
    );
    return response.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Check if company name/subdomain is available
 */
export async function checkCompanyAvailability(subdomain: string): Promise<boolean> {
  try {
    const response = await axios.get<CompanyAvailabilityResponse>(
      `${AUTH_ENDPOINTS.checkCompany}?subdomain=${encodeURIComponent(subdomain)}`
    );
    return response.data.available;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Accept invitation and complete registration
 */
export async function acceptInvitation(
  token: string,
  first_name: string,
  last_name: string,
  password: string
): Promise<{ success: boolean; message: string }> {
  try {
    const response = await axios.post<{ success: boolean; message: string }>(
      AUTH_ENDPOINTS.acceptInvitation,
      { token, first_name, last_name, password }
    );
    return response.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Change password for authenticated user
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
  token: string
): Promise<{ success: boolean; message: string }> {
  try {
    const response = await axios.post<{ success: boolean; message: string }>(
      AUTH_ENDPOINTS.changePassword,
      {
        current_password: currentPassword,
        new_password: newPassword
      },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data;
  } catch (error) {
    handleAPIError(error);
  }
}

export default {
  register,
  login,
  logout,
  getProfile,
  updateProfile,
  verifyEmail,
  forgotPassword,
  resetPassword,
  resendVerification,
  acceptInvitation,
  checkCompanyAvailability,
  changePassword,
  setAuthToken
};
